/**
 * Pure drag-mode decision kernel for the sidebar — extracted verbatim
 * (behavior-preserving) from the inline `mousedown` handler inside the
 * "exit drag mode when clicking outside" effect in layout/Sidebar.tsx. Isolates
 * the DOM-target classification so it can be characterization-tested without a
 * full React render.
 *
 * The effect's listener wiring (add/remove on `mousedown`, gated by `isDragMode`)
 * lives in `useDragModeEffects`; this file holds only the deterministic
 * "should this click exit drag mode?" predicate.
 */

/** Minimal DOM-element surface this kernel touches. */
export interface ClickTargetLike {
  closest(selector: string): unknown;
}

/** Minimal sidebar-container surface (the `sidebarRef.current`). */
export interface SidebarContainerLike {
  contains(node: unknown): boolean;
}

/**
 * Decide whether a `mousedown` should exit drag mode. Verbatim port of the
 * inline branch logic:
 *
 *   1. If the click is NOT inside the sidebar (container null, or
 *      `container.contains(target)` is false) → exit (true). This matches the
 *      original `const isInsideSidebar = sidebarRef.current?.contains(target)`
 *      which is `undefined` (falsy) when the ref is null.
 *   2. Otherwise, exit only if the target is NOT within a `.conversation-item`,
 *      `.drag-handle`, or `.delete-conversation-btn` (archive button).
 */
export function shouldExitDragModeOnClick(
  target: ClickTargetLike,
  container: SidebarContainerLike | null,
): boolean {
  const isInsideSidebar = container ? container.contains(target) : undefined;

  if (!isInsideSidebar) {
    return true;
  }

  const isConversationItem = target.closest('.conversation-item');
  const isDragHandle = target.closest('.drag-handle');
  const isArchiveButton = target.closest('.delete-conversation-btn');

  return !isConversationItem && !isDragHandle && !isArchiveButton;
}
